import { createContext, type ReactNode, useContext, useMemo } from "react";

import { DEFAULT_LOCALE } from "./constants";
import { I18N_TRANSLATION_CATALOGS } from "./generated/translationCatalogs";
import { useTranslationSettings } from "./settings/useTranslationSettings";
import type { SupportedLocale, TranslationCatalogs } from "./types";

export interface I18nContextValue {
  catalogs: TranslationCatalogs;
  fallbackLocale: SupportedLocale;
  isEnabled: boolean;
  locale: SupportedLocale;
}

interface I18nProviderProps {
  catalogs?: TranslationCatalogs;
  children: ReactNode;
  fallbackLocale?: SupportedLocale;
  isEnabled?: boolean;
  locale?: SupportedLocale;
}

const DEFAULT_CONTEXT_VALUE: I18nContextValue = {
  catalogs: I18N_TRANSLATION_CATALOGS,
  fallbackLocale: DEFAULT_LOCALE,
  isEnabled: false,
  locale: DEFAULT_LOCALE,
};

const I18nContext = createContext<I18nContextValue>(DEFAULT_CONTEXT_VALUE);

export function I18nProvider(props: I18nProviderProps) {
  const settings = useTranslationSettings();
  const locale = props.locale ?? settings.settings.locale;

  const value = useMemo<I18nContextValue>(
    () => ({
      catalogs: props.catalogs ?? I18N_TRANSLATION_CATALOGS,
      fallbackLocale: props.fallbackLocale ?? settings.settings.fallbackLocale,
      isEnabled: props.isEnabled ?? locale !== DEFAULT_LOCALE,
      locale,
    }),
    [
      props.catalogs,
      props.fallbackLocale,
      props.isEnabled,
      locale,
      settings.settings.fallbackLocale,
    ]
  );

  return <I18nContext.Provider value={value}>{props.children}</I18nContext.Provider>;
}

export function useI18nContext(): I18nContextValue {
  return useContext(I18nContext);
}
